import apiClient from './ApiClient';
import ErrorHandler from '../utils/ErrorHandler';
import { API_CONFIG } from '../config/api';

/**
 * Data Service - Handles all business data operations
 * Provides methods for projects, inquiries, employees, and dashboard data
 */
class DataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
  }

  /**
   * Get cached data if available and not expired
   */
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * Set data in cache
   */
  setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Execute request with deduplication - prevents multiple simultaneous requests for same data
   */
  async executeRequest(key, requestFn) {
    // Check cache first
    const cached = this.getCachedData(key);
    if (cached) {
      console.log(`[DataService] Using cached data for: ${key}`);
      return cached;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      console.log(`[DataService] Request already pending for: ${key}`);
      return this.pendingRequests.get(key);
    }

    console.log(`[DataService] Making new API request for: ${key}`);
    
    // Execute request and cache the promise
    const requestPromise = requestFn().then(data => {
      console.log(`[DataService] Request completed for: ${key}`);
      this.setCachedData(key, data);
      this.pendingRequests.delete(key);
      return data;
    }).catch(error => {
      console.error(`[DataService] Request failed for: ${key}`, error);
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, requestPromise);
    return requestPromise;
  }

  // ==================== DASHBOARD ====================

  /**
   * Get dashboard counts/statistics
   */
  async getDashboardCounts() {
    const cacheKey = 'dashboard_counts';
    
    return this.executeRequest(cacheKey, async () => {
      try {
        return await apiClient.get(API_CONFIG.ENDPOINTS.DASHBOARD_COUNTS);
      } catch (error) {
        throw ErrorHandler.handleApiError(error, 'Dashboard Counts');
      }
    });
  }

  // ==================== PROJECTS ====================

  /**
   * Get all projects
   */
  async getAllProjects() {
    const cacheKey = 'projects_all';
    
    return this.executeRequest(cacheKey, async () => {
      try {
        return await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ALL);
      } catch (error) {
        throw ErrorHandler.handleApiError(error, 'All Projects');
      }
    });
  }

  /**
   * Get ongoing projects
   */
  async getOngoingProjects() {
    const cacheKey = 'projects_ongoing';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ONGOING);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Ongoing Projects');
    }
  }

  /**
   * Get ongoing projects list (simplified)
   */
  async getOngoingProjectsList() {
    const cacheKey = 'projects_ongoing_list';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ONGOING_LIST);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Ongoing Projects List');
    }
  }

  /**
   * Get completed projects
   */
  async getCompletedProjects() {
    const cacheKey = 'projects_completed';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_COMPLETED);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Completed Projects');
    }
  }

  /**
   * Get project by ID
   */
  async getProjectById(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const cacheKey = `project_${projectId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_BY_ID(projectId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Project ${projectId}`);
    }
  }

  /**
   * Create new project
   */
  async createProject(projectData) {
    if (!projectData) {
      throw new Error('Project data is required');
    }

    try {
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.PROJECTS_CREATE, projectData);
      // Clear related cache
      this.clearProjectsCache();
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Create Project');
    }
  }

  /**
   * Update project
   */
  async updateProject(projectId, projectData) {
    if (!projectId || !projectData) {
      throw new Error('Project ID and data are required');
    }

    try {
      const data = await apiClient.put(API_CONFIG.ENDPOINTS.PROJECTS_BY_ID(projectId), projectData);
      // Clear related cache
      this.clearProjectsCache();
      this.cache.delete(`project_${projectId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Update Project ${projectId}`);
    }
  }

  /**
   * Delete project
   */
  async deleteProject(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    try {
      const data = await apiClient.delete(API_CONFIG.ENDPOINTS.PROJECTS_BY_ID(projectId));
      // Clear related cache
      this.clearProjectsCache();
      this.cache.delete(`project_${projectId}`);
      // Return success object (ApiClient now handles 204 and empty responses)
      return data || { success: true, message: 'Project deleted successfully' };
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Delete Project ${projectId}`);
    }
  }

  /**
   * Link project with client
   */
  async linkProjectClient(projectId, clientId) {
    if (!projectId || !clientId) {
      throw new Error('Project ID and Client ID are required');
    }

    try {
      const data = await apiClient.patch(API_CONFIG.ENDPOINTS.PROJECTS_LINK_CLIENT(projectId), { client_id: clientId });
      // Clear related cache
      this.clearProjectsCache();
      this.cache.delete(`project_${projectId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Link Project ${projectId} with Client ${clientId}`);
    }
  }

  /**
   * Get project by ID
   */
  async getProjectById(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const cacheKey = `project_${projectId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_BY_ID(projectId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Get Project ${projectId}`);
    }
  }

  /**
   * Get project updates
   */
  async getProjectUpdates(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const cacheKey = `project_${projectId}_updates`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_UPDATES(projectId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      // If endpoint doesn't exist, return empty array
      if (error.status === 404) {
        return [];
      }
      throw ErrorHandler.handleApiError(error, `Get Project Updates ${projectId}`);
    }
  }

  /**
   * Create project update with optional files
   */
  async createProjectUpdate(projectId, updateData, files = []) {
    if (!projectId || !updateData) {
      throw new Error('Project ID and update data are required');
    }

    try {
      // If files are provided, use FormData
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('update_text', updateData.update_text);
        if (updateData.room_name) {
          formData.append('room_name', updateData.room_name);
        }
        
        files.forEach((file) => {
          formData.append('files', file);
        });

        const data = await apiClient.post(
          API_CONFIG.ENDPOINTS.PROJECTS_UPDATES(projectId),
          formData,
          {
            isFormData: true
          }
        );
        
        // Clear updates cache
        this.cache.delete(`project_${projectId}_updates`);
        return data;
      } else {
        // No files, use regular JSON
        const data = await apiClient.post(API_CONFIG.ENDPOINTS.PROJECTS_UPDATES(projectId), updateData);
        
        // Clear updates cache
        this.cache.delete(`project_${projectId}_updates`);
        return data;
      }
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Create Project Update ${projectId}`);
    }
  }

  /**
   * Get project rooms
   */
  async getProjectRooms(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const cacheKey = `project_${projectId}_rooms`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ROOMS(projectId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      // If endpoint doesn't exist, return empty array
      if (error.status === 404) {
        return [];
      }
      throw ErrorHandler.handleApiError(error, `Get Project Rooms ${projectId}`);
    }
  }

  /**
   * Create a new room for a project
   */
  async createProjectRoom(projectId, roomData) {
    if (!projectId || !roomData) {
      throw new Error('Project ID and room data are required');
    }

    try {
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.PROJECTS_ROOMS(projectId), {
        room_name: roomData.room_name,
        description: roomData.description || ''
      });
      
      // Clear rooms cache
      this.cache.delete(`project_${projectId}_rooms`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Create Project Room ${projectId}`);
    }
  }

  /**
   * Get room details with timeline images
   */
  async getRoomDetails(projectId, roomId) {
    if (!projectId || !roomId) {
      throw new Error('Project ID and Room ID are required');
    }

    const cacheKey = `room_${projectId}_${roomId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ROOM_BY_ID(projectId, roomId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Get Room Details ${projectId}/${roomId}`);
    }
  }

  /**
   * Get room timeline with all images
   */
  async getRoomTimeline(projectId, roomId) {
    if (!projectId || !roomId) {
      throw new Error('Project ID and Room ID are required');
    }

    const cacheKey = `room_${projectId}_${roomId}_timeline`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.PROJECTS_ROOM_TIMELINE(projectId, roomId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Get Room Timeline ${projectId}/${roomId}`);
    }
  }

  /**
   * Update room details
   */
  async updateRoom(projectId, roomId, roomData) {
    if (!projectId || !roomId || !roomData) {
      throw new Error('Project ID, Room ID, and room data are required');
    }

    try {
      const data = await apiClient.patch(
        API_CONFIG.ENDPOINTS.PROJECTS_ROOM_BY_ID(projectId, roomId),
        {
          room_name: roomData.room_name,
          description: roomData.description || ''
        }
      );
      
      // Clear room caches
      this.cache.delete(`room_${projectId}_${roomId}`);
      this.cache.delete(`room_${projectId}_${roomId}_timeline`);
      this.cache.delete(`project_${projectId}_rooms`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Update Room ${projectId}/${roomId}`);
    }
  }

  /**
   * Delete room
   */
  async deleteRoom(projectId, roomId) {
    if (!projectId || !roomId) {
      throw new Error('Project ID and Room ID are required');
    }

    try {
      const data = await apiClient.delete(API_CONFIG.ENDPOINTS.PROJECTS_ROOM_BY_ID(projectId, roomId));
      
      // Clear room caches
      this.cache.delete(`room_${projectId}_${roomId}`);
      this.cache.delete(`room_${projectId}_${roomId}_timeline`);
      this.cache.delete(`project_${projectId}_rooms`);
      
      // Return success object (ApiClient handles 204 and empty responses)
      return data || { success: true, message: 'Room deleted successfully' };
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Delete Room ${projectId}/${roomId}`);
    }
  }

  /**
   * Add timeline image to a room
   */
  async addRoomTimelineImage(projectId, roomId, file, description = '') {
    if (!projectId || !roomId || !file) {
      throw new Error('Project ID, Room ID, and file are required');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const data = await apiClient.post(
        API_CONFIG.ENDPOINTS.PROJECTS_ROOM_TIMELINE_IMAGES(projectId, roomId),
        formData,
        {
          isFormData: true
        }
      );
      
      // Clear room caches to refresh timeline images
      this.cache.delete(`room_${projectId}_${roomId}`);
      this.cache.delete(`room_${projectId}_${roomId}_timeline`);
      this.cache.delete(`project_${projectId}_rooms`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Add Room Timeline Image ${projectId}/${roomId}`);
    }
  }

  /**
   * Create room update with images and remarks
   */
  async createRoomUpdate(projectId, roomId, remarks = '', files = []) {
    if (!projectId || !roomId) {
      throw new Error('Project ID and Room ID are required');
    }

    try {
      const formData = new FormData();
      if (remarks) {
        formData.append('remarks', remarks);
      }
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      const data = await apiClient.post(
        API_CONFIG.ENDPOINTS.PROJECTS_ROOM_UPDATES(projectId, roomId),
        formData,
        {
          isFormData: true
        }
      );
      
      // Clear room caches to refresh updates
      this.cache.delete(`room_${projectId}_${roomId}`);
      this.cache.delete(`room_${projectId}_${roomId}_timeline`);
      this.cache.delete(`project_${projectId}_rooms`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Create Room Update ${projectId}/${roomId}`);
    }
  }

  /**
   * Clear projects-related cache
   */
  clearProjectsCache() {
    const projectKeys = ['projects_all', 'projects_ongoing', 'projects_ongoing_list', 'projects_completed', 'dashboard_counts'];
    projectKeys.forEach(key => this.cache.delete(key));
  }

  // ==================== INQUIRIES ====================

  /**
   * Get all inquiries
   */
  async getAllInquiries() {
    const cacheKey = 'inquiries_all';
    
    return this.executeRequest(cacheKey, async () => {
      try {
        return await apiClient.get(API_CONFIG.ENDPOINTS.INQUIRIES_ALL);
      } catch (error) {
        throw ErrorHandler.handleApiError(error, 'All Inquiries');
      }
    });
  }

  /**
   * Get recent inquiries
   */
  async getRecentInquiries() {
    const cacheKey = 'inquiries_recent';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.INQUIRIES_RECENT);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Recent Inquiries');
    }
  }

  /**
   * Get inquiry by ID
   */
  async getInquiryById(inquiryId) {
    if (!inquiryId) {
      throw new Error('Inquiry ID is required');
    }

    const cacheKey = `inquiry_${inquiryId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.INQUIRIES_BY_ID(inquiryId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Inquiry ${inquiryId}`);
    }
  }

  /**
   * Create new inquiry/contact
   */
  async createInquiry(inquiryData) {
    if (!inquiryData) {
      throw new Error('Inquiry data is required');
    }

    try {
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.INQUIRIES_CREATE, inquiryData);
      // Clear related cache
      this.clearInquiriesCache();
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Create Inquiry');
    }
  }

  /**
   * Update inquiry
   */
  async updateInquiry(inquiryId, inquiryData) {
    if (!inquiryId || !inquiryData) {
      throw new Error('Inquiry ID and data are required');
    }

    try {
      const data = await apiClient.put(API_CONFIG.ENDPOINTS.INQUIRIES_BY_ID(inquiryId), inquiryData);
      // Clear related cache
      this.clearInquiriesCache();
      this.cache.delete(`inquiry_${inquiryId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Update Inquiry ${inquiryId}`);
    }
  }

  /**
   * Delete inquiry
   */
  async deleteInquiry(inquiryId) {
    if (!inquiryId) {
      throw new Error('Inquiry ID is required');
    }

    try {
      const data = await apiClient.delete(API_CONFIG.ENDPOINTS.INQUIRIES_BY_ID(inquiryId));
      // Clear related cache
      this.clearInquiriesCache();
      this.cache.delete(`inquiry_${inquiryId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Delete Inquiry ${inquiryId}`);
    }
  }

  /**
   * Clear inquiries-related cache
   */
  clearInquiriesCache() {
    const inquiryKeys = ['inquiries_all', 'inquiries_recent', 'dashboard_counts'];
    inquiryKeys.forEach(key => this.cache.delete(key));
  }

  // ==================== EMPLOYEES ====================

  /**
   * Get all employees
   */
  async getAllEmployees() {
    const cacheKey = 'employees_all';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.EMPLOYEES_ALL);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'All Employees');
    }
  }

  /**
   * Get free employees
   */
  async getFreeEmployees() {
    const cacheKey = 'employees_free';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.EMPLOYEES_FREE);
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Free Employees');
    }
  }

  /**
   * Get employee by ID
   */
  async getEmployeeById(employeeId) {
    if (!employeeId) {
      throw new Error('Employee ID is required');
    }

    const cacheKey = `employee_${employeeId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.EMPLOYEES_BY_ID(employeeId));
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Employee ${employeeId}`);
    }
  }

  /**
   * Create new employee
   */
  async createEmployee(employeeData) {
    if (!employeeData) {
      throw new Error('Employee data is required');
    }

    try {
      console.log('[DataService] Creating employee with data:', employeeData);
      console.log('[DataService] Using endpoint:', API_CONFIG.ENDPOINTS.EMPLOYEES_CREATE);
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.EMPLOYEES_CREATE, employeeData);
      console.log('[DataService] Employee created successfully:', data);
      // Clear related cache
      this.clearEmployeesCache();
      return data;
    } catch (error) {
      console.error('[DataService] Error creating employee:', error);
      console.error('[DataService] Error status:', error.status);
      console.error('[DataService] Error data:', error.data);
      throw ErrorHandler.handleApiError(error, 'Create Employee');
    }
  }

  /**
   * Update employee
   */
  async updateEmployee(employeeId, employeeData) {
    if (!employeeId || !employeeData) {
      throw new Error('Employee ID and data are required');
    }

    try {
      const data = await apiClient.put(API_CONFIG.ENDPOINTS.EMPLOYEES_BY_ID(employeeId), employeeData);
      // Clear related cache
      this.clearEmployeesCache();
      this.cache.delete(`employee_${employeeId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Update Employee ${employeeId}`);
    }
  }

  /**
   * Delete employee
   */
  async deleteEmployee(employeeId) {
    if (!employeeId) {
      throw new Error('Employee ID is required');
    }

    try {
      const data = await apiClient.delete(API_CONFIG.ENDPOINTS.EMPLOYEES_BY_ID(employeeId));
      // Clear related cache
      this.clearEmployeesCache();
      this.cache.delete(`employee_${employeeId}`);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Delete Employee ${employeeId}`);
    }
  }

  /**
   * Assign employee to project
   */
  async assignEmployeeToProject(employeeId, projectId) {
    if (!employeeId || !projectId) {
      throw new Error('Employee ID and Project ID are required');
    }

    try {
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.EMPLOYEES_ASSIGN(employeeId, projectId));
      // Clear related cache
      this.clearEmployeesCache();
      this.clearProjectsCache();
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Assign Employee ${employeeId} to Project ${projectId}`);
    }
  }

  /**
   * Unassign employee from project
   */
  async unassignEmployeeFromProject(employeeId, projectId) {
    if (!employeeId || !projectId) {
      throw new Error('Employee ID and Project ID are required');
    }

    try {
      const data = await apiClient.delete(API_CONFIG.ENDPOINTS.EMPLOYEES_UNASSIGN(employeeId, projectId));
      // Clear related cache
      this.clearEmployeesCache();
      this.clearProjectsCache();
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Unassign Employee ${employeeId} from Project ${projectId}`);
    }
  }

  /**
   * Clear employees-related cache
   */
  clearEmployeesCache() {
    const employeeKeys = ['employees_all', 'employees_free'];
    employeeKeys.forEach(key => this.cache.delete(key));
  }

  // ==================== EMPLOYEE PORTAL ====================

  /**
   * Employee login
   */
  async employeeLogin(credentials) {
    if (!credentials || !credentials.username || !credentials.password) {
      throw new Error('Username and password are required');
    }

    try {
      const data = await apiClient.post(API_CONFIG.ENDPOINTS.EMPLOYEE_LOGIN, credentials, { skipAuth: true });
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Employee Login');
    }
  }

  /**
   * Get employee profile
   */
  async getEmployeeProfile() {
    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.EMPLOYEE_PROFILE);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Employee Profile');
    }
  }

  /**
   * Update employee profile
   */
  async updateEmployeeProfile(profileData) {
    if (!profileData) {
      throw new Error('Profile data is required');
    }

    try {
      const data = await apiClient.put(API_CONFIG.ENDPOINTS.EMPLOYEE_PROFILE, profileData);
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Update Employee Profile');
    }
  }

  // ==================== CLIENTS ====================

  /**
   * Get all clients
   */
  async getAllClients() {
    const cacheKey = 'clients_all';
    
    return this.executeRequest(cacheKey, async () => {
      try {
        return await apiClient.get(API_CONFIG.ENDPOINTS.CLIENTS_ALL);
      } catch (error) {
        throw ErrorHandler.handleApiError(error, 'All Clients');
      }
    });
  }

  /**
   * Clear clients-related cache
   */
  clearClientsCache() {
    const clientKeys = ['clients_all'];
    clientKeys.forEach(key => this.cache.delete(key));
  }

  // ==================== SYSTEM ====================

  /**
   * Health check
   */
  async healthCheck() {
    try {
      const data = await apiClient.get(API_CONFIG.ENDPOINTS.HEALTH, { skipAuth: true });
      return data;
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Health Check');
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Refresh all data (clear cache and refetch)
   */
  async refreshAllData() {
    this.clearCache();
    
    try {
      // Fetch fresh data for dashboard
      const [counts, projects, inquiries] = await Promise.allSettled([
        this.getDashboardCounts(),
        this.getAllProjects(),
        this.getAllInquiries()
      ]);

      return {
        success: true,
        message: 'Data refreshed successfully',
        results: { counts, projects, inquiries }
      };
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Refresh All Data');
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cachedKeys: Array.from(this.cache.keys()),
      pendingKeys: Array.from(this.pendingRequests.keys()),
      cacheTimeout: this.cacheTimeout
    };
  }

  /**
   * Log current cache status (for debugging)
   */
  logCacheStatus() {
    const stats = this.getCacheStats();
    console.log('[DataService] Cache Status:', stats);
  }
}

// Export singleton instance
const dataService = new DataService();
export default dataService;